import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { useCalendars, useCreateCalendar, useUpdateCalendar, useDeleteCalendar } from '../hooks/useCalendars'
import { useCalendarVisibility } from '../hooks/useCalendarVisibility'
import { useLinkedHouseholds } from '../../linkedHouseholds/hooks/useLinkedHouseholds'
import { useSharedCalendars } from '../../linkedHouseholds/hooks/useSharedCalendars'
import { useExternalCalendars } from '../hooks/useExternalCalendars'
import { useAuth } from '../../../hooks/useAuth'
import { useProfile } from '../../../hooks/useProfile'
import { Button } from '../../../components/ui/Button'
import { Spinner } from '../../../components/ui/Spinner'
import { cn } from '../../../lib/utils'
import { memberColors, EVENT_PALETTE } from '../../../styles/tokens'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL

const ICalIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
)

const LinkBadge = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
  </svg>
)

const ColorPicker = ({ value, onChange }) => (
  <div className="flex gap-1.5 flex-wrap mt-2">
    {EVENT_PALETTE.map((c) => (
      <button key={c} type="button" onClick={() => onChange(c)}
        className={cn('w-6 h-6 rounded-full transition-transform', value === c && 'ring-2 ring-offset-1 ring-primary scale-110')}
        style={{ backgroundColor: c }} />
    ))}
  </div>
)

// ── Own calendar row ──────────────────────────────────────────────────────────
const CalendarRow = ({ cal, visibility, onToggle, showShare, isSharedOut, onToggleShare }) => {
  const updateCalendar = useUpdateCalendar()
  const deleteCalendar = useDeleteCalendar()
  const [editing,       setEditing]       = useState(false)
  const [name,          setName]          = useState(cal.name)
  const [color,         setColor]         = useState(cal.color)
  const [copied,        setCopied]        = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const visible = visibility.isVisible(cal.id)

  const feedUrl = `${SUPABASE_URL}/functions/v1/ical-feed/${cal.id}`

  const copyFeed = async () => {
    try { await navigator.clipboard.writeText(feedUrl) } catch { /* ignore */ }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const save = async () => {
    await updateCalendar.mutateAsync({ id: cal.id, name, color })
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="bg-background rounded-xl p-4 space-y-3">
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
          className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
        <ColorPicker value={color} onChange={setColor} />
        <div className="flex gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
          <Button type="button" size="sm" onClick={save} disabled={updateCalendar.isPending}>
            {updateCalendar.isPending ? <Spinner size="sm" /> : 'Save'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3 py-3 border-b border-border/50 last:border-0">
      <button onClick={() => onToggle(cal.id)} className="flex-shrink-0" title={visible ? 'Hide' : 'Show'}>
        <span className={cn('w-4 h-4 rounded border-2 flex items-center justify-center transition-colors',
          visible ? 'border-transparent' : 'border-border bg-transparent')}
          style={{ backgroundColor: visible ? cal.color : 'transparent' }}>
          {visible && <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2"><path d="m2 6 3 3 5-5"/></svg>}
        </span>
      </button>

      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cal.color }} />
      <span className="flex-1 text-sm font-medium text-text-primary">{cal.name}</span>

      {/* Share toggle (only when linked household exists) */}
      {showShare && (
        <button
          onClick={() => onToggleShare(cal.id)}
          title={isSharedOut ? 'Shared with linked household' : 'Share with linked household'}
          className={cn('transition-colors', isSharedOut ? 'text-primary' : 'text-text-muted hover:text-primary')}
        >
          <LinkBadge />
        </button>
      )}

      {/* iCal copy */}
      <button onClick={copyFeed} className="text-text-muted hover:text-primary transition-colors" title="Copy iCal feed URL">
        {copied
          ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m5 12 5 5L20 7"/></svg>
          : <ICalIcon />}
      </button>

      {/* Edit */}
      <button onClick={() => setEditing(true)} className="text-text-muted hover:text-primary transition-colors" title="Edit calendar">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
      </button>

      {/* Delete */}
      {confirmDelete ? (
        <div className="flex gap-1">
          <button onClick={() => setConfirmDelete(false)} className="text-xs text-text-muted">Cancel</button>
          <button onClick={() => deleteCalendar.mutateAsync(cal.id)} className="text-xs text-rose-600 font-semibold">Delete</button>
        </div>
      ) : (
        <button onClick={() => setConfirmDelete(true)} className="text-text-muted hover:text-rose-500 transition-colors" title="Delete calendar">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </button>
      )}
    </div>
  )
}

// ── Shared-in calendar row (read-only) ────────────────────────────────────────
const SharedCalendarRow = ({ cal, visibility, onToggle }) => {
  const visible = visibility.isVisible(cal.id)
  return (
    <div className="flex items-center gap-3 py-3 border-b border-border/50 last:border-0">
      <button onClick={() => onToggle(cal.id)} className="flex-shrink-0" title={visible ? 'Hide' : 'Show'}>
        <span className={cn('w-4 h-4 rounded border-2 flex items-center justify-center transition-colors',
          visible ? 'border-transparent' : 'border-border bg-transparent')}
          style={{ backgroundColor: visible ? cal.color : 'transparent' }}>
          {visible && <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2"><path d="m2 6 3 3 5-5"/></svg>}
        </span>
      </button>
      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cal.color }} />
      <span className="flex-1 text-sm font-medium text-text-primary">{cal.name}</span>
      <span className="text-primary"><LinkBadge /></span>
    </div>
  )
}

// ── System calendar row (synthetic overlays) ─────────────────────────────────
const SYSTEM_CALENDARS = [
  { id: 'system:tasks',     label: 'Tasks',     color: '#10b981', emoji: '✓' },
  { id: 'system:meals',     label: 'Meals',     color: '#f97316', emoji: '🍽' },
  { id: 'system:birthdays', label: 'Birthdays', color: '#ec4899', emoji: '🎂' },
  { id: 'system:bills',     label: 'Bills',     color: '#3b82f6', emoji: '💵' },
]

const SystemCalendarRow = ({ cal, visibility }) => {
  const visible = visibility.isVisible(cal.id)
  return (
    <div className="flex items-center gap-3 py-3 border-b border-border/50 last:border-0">
      <button onClick={() => visibility.toggleCalendar(cal.id)} className="flex-shrink-0" title={visible ? 'Hide' : 'Show'}>
        <span className={cn('w-4 h-4 rounded border-2 flex items-center justify-center transition-colors',
          visible ? 'border-transparent' : 'border-border bg-transparent')}
          style={{ backgroundColor: visible ? cal.color : 'transparent' }}>
          {visible && <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2"><path d="m2 6 3 3 5-5"/></svg>}
        </span>
      </button>
      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cal.color }} />
      <span className="flex-1 text-sm font-medium text-text-primary">{cal.label}</span>
      <span className="text-xs text-text-muted">{cal.emoji}</span>
    </div>
  )
}

// ── External calendar row (ICS subscription) ─────────────────────────────────
const ExternalCalendarRow = ({ cal, visibility, onToggle, onSync, onDelete, isSyncing }) => {
  const visible = visibility.isVisible(cal.id)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const lastSynced = cal.last_synced ? format(parseISO(cal.last_synced), 'MMM d, h:mm a') : null

  return (
    <div className="flex items-center gap-3 py-3 border-b border-border/50 last:border-0">
      <button onClick={() => onToggle(cal.id)} className="flex-shrink-0" title={visible ? 'Hide' : 'Show'}>
        <span className={cn('w-4 h-4 rounded border-2 flex items-center justify-center transition-colors',
          visible ? 'border-transparent' : 'border-border bg-transparent')}
          style={{ backgroundColor: visible ? cal.color : 'transparent' }}>
          {visible && <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2"><path d="m2 6 3 3 5-5"/></svg>}
        </span>
      </button>
      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cal.color }} />
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-text-primary block truncate">{cal.name}</span>
        <span className="text-[10px] text-text-muted">{lastSynced ? `Synced ${lastSynced}` : 'Never synced'}</span>
      </div>
      {/* Sync */}
      <button onClick={() => onSync(cal.id)} disabled={isSyncing}
        className="text-text-muted hover:text-primary transition-colors disabled:opacity-40" title="Sync now">
        {isSyncing
          ? <Spinner size="sm" />
          : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
        }
      </button>
      {/* Delete */}
      {confirmDelete ? (
        <div className="flex gap-1">
          <button onClick={() => setConfirmDelete(false)} className="text-xs text-text-muted">Cancel</button>
          <button onClick={() => onDelete(cal.id)} className="text-xs text-rose-600 font-semibold">Delete</button>
        </div>
      ) : (
        <button onClick={() => setConfirmDelete(true)} className="text-text-muted hover:text-rose-500 transition-colors" title="Remove subscription">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </button>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
export const CalendarManager = ({ onClose }) => {
  const { isAdmin } = useAuth()
  const { data: profile } = useProfile()
  const defaultCalColor = memberColors[profile?.member_color] ?? EVENT_PALETTE[0]
  const { data: ownCalendars = [], isLoading } = useCalendars()
  const createCalendar  = useCreateCalendar()
  const visibility      = useCalendarVisibility()
  const { activeLink }  = useLinkedHouseholds()
  const { incoming, isSharedOut, toggleShare } = useSharedCalendars()
  const { data: externalCals = [], addCalendar: addExtCal, deleteCalendar: deleteExtCal, syncCalendar } = useExternalCalendars()

  const [creating,   setCreating]  = useState(false)
  const [newName,    setNewName]   = useState('')
  const [newColor,   setNewColor]  = useState(EVENT_PALETTE[0])
  const [addingExt,  setAddingExt] = useState(false)
  const [extName,    setExtName]   = useState('')
  const [extUrl,     setExtUrl]    = useState('')
  const [extColor,   setExtColor]  = useState('#64748b')
  const [syncingId,  setSyncingId] = useState(null)

  const handleCreate = async (e) => {
    e.preventDefault()
    if (!newName.trim()) return
    await createCalendar.mutateAsync({ name: newName.trim(), color: newColor })
    setNewName('')
    setNewColor(defaultCalColor)
    setCreating(false)
  }

  const handleAddExt = async (e) => {
    e.preventDefault()
    if (!extName.trim() || !extUrl.trim()) return
    await addExtCal.mutateAsync({ name: extName.trim(), ics_url: extUrl.trim(), color: extColor })
    setExtName('')
    setExtUrl('')
    setExtColor('#64748b')
    setAddingExt(false)
  }

  const handleSync = async (id) => {
    setSyncingId(id)
    try { await syncCalendar.mutateAsync(id) } finally { setSyncingId(null) }
  }

  const linkedHouseholdId = activeLink?.isPrimary
    ? activeLink.linked_household_id
    : activeLink?.primary_household_id

  const incomingCals = incoming.map((s) => ({ ...s.calendars, fromHouseholdName: s.from_household?.name }))

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[85dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
          <h2 className="text-base font-bold text-text-primary">Calendars</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4">
          {isLoading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : (
            <>
              {ownCalendars.map((cal) => (
                <CalendarRow
                  key={cal.id}
                  cal={cal}
                  visibility={visibility}
                  onToggle={visibility.toggleCalendar}
                  showShare={isAdmin && !!activeLink}
                  isSharedOut={isSharedOut(cal.id)}
                  onToggleShare={() => toggleShare(cal.id, linkedHouseholdId)}
                />
              ))}

              {/* Shared from linked household */}
              {incomingCals.length > 0 && (
                <div className="mt-5">
                  <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <LinkBadge /> From {incomingCals[0]?.fromHouseholdName ?? 'linked household'}
                  </p>
                  {incomingCals.map((cal) => (
                    <SharedCalendarRow
                      key={cal.id}
                      cal={cal}
                      visibility={visibility}
                      onToggle={visibility.toggleCalendar}
                    />
                  ))}
                </div>
              )}

              {/* System calendars — synthetic overlays */}
              <div className="mt-5">
                <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">System</p>
                {SYSTEM_CALENDARS.map((cal) => (
                  <SystemCalendarRow key={cal.id} cal={cal} visibility={visibility} />
                ))}
              </div>

              {/* External calendars (ICS subscriptions) */}
              {externalCals.length > 0 && (
                <div className="mt-5">
                  <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">External</p>
                  {externalCals.map((cal) => (
                    <ExternalCalendarRow
                      key={cal.id}
                      cal={cal}
                      visibility={visibility}
                      onToggle={visibility.toggleCalendar}
                      onSync={handleSync}
                      onDelete={deleteExtCal.mutateAsync}
                      isSyncing={syncingId === cal.id}
                    />
                  ))}
                </div>
              )}

              {creating ? (
                <form onSubmit={handleCreate} className="mt-4 bg-background rounded-xl p-4 space-y-3">
                  <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                    placeholder="Calendar name"
                    className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                  <ColorPicker value={newColor} onChange={setNewColor} />
                  <div className="flex gap-2">
                    <Button type="button" variant="secondary" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
                    <Button type="submit" size="sm" disabled={createCalendar.isPending}>
                      {createCalendar.isPending ? <Spinner size="sm" /> : 'Create'}
                    </Button>
                  </div>
                </form>
              ) : (
                <button onClick={() => { setNewColor(defaultCalColor); setCreating(true) }}
                  className="mt-4 flex items-center gap-2 text-sm text-primary font-medium hover:text-primary/80 transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                  New calendar
                </button>
              )}

              {addingExt ? (
                <form onSubmit={handleAddExt} className="mt-3 bg-background rounded-xl p-4 space-y-3">
                  <input autoFocus value={extName} onChange={(e) => setExtName(e.target.value)}
                    placeholder="Calendar name"
                    className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                  <input value={extUrl} onChange={(e) => setExtUrl(e.target.value)}
                    placeholder="https://example.com/calendar.ics"
                    className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                  <ColorPicker value={extColor} onChange={setExtColor} />
                  <div className="flex gap-2">
                    <Button type="button" variant="secondary" size="sm" onClick={() => setAddingExt(false)}>Cancel</Button>
                    <Button type="submit" size="sm" disabled={addExtCal.isPending}>
                      {addExtCal.isPending ? <Spinner size="sm" /> : 'Subscribe'}
                    </Button>
                  </div>
                </form>
              ) : (
                <button onClick={() => setAddingExt(true)}
                  className="mt-3 flex items-center gap-2 text-sm text-text-muted font-medium hover:text-primary transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                  Subscribe to external calendar
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
